import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import InputComponent from '../input/input.component';
import ErrorValidateComponent from '../error-validate/error-validate.component';
import ButtonSecondary from '../button-refactor/button-secondary';
import ButtonPrimary from '../button-refactor/button-primary';
import { changePasswordAuthApi } from '../../api';
import toast from 'react-hot-toast';
import { InputWarning } from '../inputwarning/input-warning.component';
import { useSelector } from 'react-redux';
import { RootState } from '../../configuration';

interface IFormValues {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const schema = yup.object().shape({
  currentPassword: yup.string().required('Current password is required'),
  newPassword: yup
    .string()
    .required('New password is required')
    // Khớp luật máy chủ (identity: tối thiểu 8 ký tự, có chữ số).
    .min(8, 'Mật khẩu phải có ít nhất 8 ký tự')
    .matches(/\d/, 'Mật khẩu phải có ít nhất 1 chữ số'),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('newPassword')], 'Passwords must match')
    .required('Confirm password is required'),
});

const ChangePasswordForm: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<IFormValues>({
    resolver: yupResolver(schema),
  });
  const [loading, setLoading] = useState<boolean>(false);
  const userId = useSelector((state: RootState) => state.features.auth.profile?.userId);
  const onSubmit = async (data: IFormValues) => {
    if (!userId) {
      toast.error('Không xác định được tài khoản, vui lòng đăng nhập lại');
      return;
    }
    setLoading(true);
    try {
      await changePasswordAuthApi(userId, data.currentPassword, data.newPassword);
      toast.success('Thay đổi mật khẩu thành công');
      onClose();
    } catch (error) {
      toast.error('Không thể thay đổi mật khẩu. Kiểm tra lại mật khẩu hiện tại.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="flex flex-col gap-4 p-6">
        <div className="flex flex-col gap-1">
          <label
            htmlFor="currentPassword"
            className="flex items-center w-[30%] text-sm text-[#070708] dark:text-slate-50 font-normal"
          >
            Mật khẩu hiện tại &nbsp;
            <InputWarning />
          </label>
          <div className="w-full">
            <InputComponent
              type="password"
              placeholder="Mật khẩu hiện tại"
              id="currentPassword"
              register={register('currentPassword')}
            />
            <ErrorValidateComponent visible={!!errors.currentPassword}>
              {errors.currentPassword?.message}
            </ErrorValidateComponent>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <label
            htmlFor="newPassword"
            className="flex items-center w-[30%] text-sm text-[#070708] dark:text-slate-50 font-normal"
          >
            Mật khẩu mới&nbsp;
            <InputWarning />
          </label>
          <div className="w-full">
            <InputComponent
              type="password"
              placeholder="Mật khẩu mới"
              id="newPassword"
              register={register('newPassword')}
            />
            <ErrorValidateComponent visible={!!errors.newPassword}>
              {errors.newPassword?.message}
            </ErrorValidateComponent>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <label
            htmlFor="confirmPassword"
            className="flex items-center w-[30%] text-sm text-[#070708] dark:text-slate-50 font-normal"
          >
            Xác nhận mật khẩu mới&nbsp;
            <InputWarning />
          </label>
          <div className="w-full">
            <InputComponent
              type="password"
              placeholder="Xác nhận mật khẩu mới"
              id="confirmPassword"
              register={register('confirmPassword')}
            />
            <ErrorValidateComponent visible={!!errors.confirmPassword}>
              {errors.confirmPassword?.message}
            </ErrorValidateComponent>
          </div>
        </div>
      </div>

      <div className="form-group flex justify-end items-center border-t border-t-[#DEE0E6] dark:border-t-slate-700 p-6">
        <ButtonSecondary className="mr-2 !border-[#DEE0E6] dark:border-slate-700" onClick={onClose}>
          Hủy bỏ
        </ButtonSecondary>
        <ButtonPrimary loading={loading}>Lưu thay đổi</ButtonPrimary>
      </div>
    </form>
  );
};

export default ChangePasswordForm;
